<template>
  <div class="auth-shell d-flex justify-content-center align-items-center">
    <div class="card p-4 auth-card">
      <!-- Header: Back + Title -->
      <div class="auth-head">
        <router-link v-if="showBack" to="/settings" class="btn-outline back-btn">
          <i class="bi bi-arrow-left"></i> {{ $t('auth.back') }}
        </router-link>
        <h2 class="text-center mb-0 flex-1">{{ $t('auth.change.title') }}</h2>
        <!-- spacer to balance flex -->
        <span class="back-spacer" aria-hidden="true"></span>
      </div>

      <form @submit.prevent="submit" novalidate>
        <!-- Current password -->
        <div class="mb-3">
          <label for="current" class="form-label">{{ $t('auth.change.currentPassword') }}</label>
          <input
            v-model="form.current"
            type="password"
            id="current"
            class="form-control"
            autocomplete="current-password"
            required
          />
        </div>

        <!-- New password -->
        <div class="mb-3">
          <label for="newpass" class="form-label">{{ $t('auth.password.new') }}</label>
          <input
            v-model="form.new"
            type="password"
            id="newpass"
            class="form-control"
            autocomplete="new-password"
            required
          />
        </div>

        <!-- regex errors / strength hint -->
        <div v-if="form.new && passwordErrors.length" class="error-message mb-3">
          <ul class="mb-0">
            <li v-for="err in passwordErrors" :key="err">{{ err }}</li>
          </ul>
        </div>
        <div v-else-if="form.new && !isStrong" class="error-message mb-3">
          {{ $t('auth.password.tooWeakHint') }}
        </div>

        <!-- Confirm -->
        <div class="mb-3">
          <label for="confirm" class="form-label">{{ $t('auth.password.confirmNew') }}</label>
          <input
            v-model="form.confirm"
            type="password"
            id="confirm"
            class="form-control"
            autocomplete="new-password"
            required
          />
        </div>

        <div v-if="form.confirm && form.new !== form.confirm" class="error-message mb-3">
          {{ $t('auth.password.noMatch') }}
        </div>

        <!-- API error -->
        <div v-if="error" class="error-message mb-3">{{ error }}</div>

        <!-- Submit -->
        <button
          type="submit"
          class="stora-btn-primary w-100"
          :disabled="loading || !isStrong || form.new !== form.confirm"
        >
          <span>{{ loading ? $t('auth.change.saving') : $t('auth.change.submit') }}</span>
        </button>

        <!-- Success message -->
        <div v-if="success" class="success-message mt-3">
          {{ $t('auth.change.success') }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/UserStore'
import { getPasswordErrors,  isPasswordStrong} from '@/utils/passwordPolicy'

export default {
    name: 'ChangePassword',

    setup() {
        const userStore = useUserStore()
        const { t } = useI18n()
        const router = useRouter()
        const route = useRoute()

        const form = ref({ current: '', new: '', confirm: '' })
        const error = ref(null)
        const success = ref(false)
        const loading = ref(false)
        const showBack = route.query.from === 'settings'

        // your per-rule feedback
        const passwordErrors = computed(() => getPasswordErrors(form.value.new))
        // overall strength including zxcvbn
        const isStrong = computed(() => isPasswordStrong(form.value.new))

        const submit = async () => {
            error.value = null
            success.value = false

            if (form.value.new !== form.value.confirm) {
                error.value = t('auth.password.mismatch')
                return
            }

            // final guard
            if (!isStrong.value) {
                error.value = t('auth.password.tooWeak')
                return
            }

            loading.value = true
            try {
                await userStore.changePassword({
                oldPassword: form.value.current,
                newPassword: form.value.new
                })
                success.value = true
                form.value = { current: '', new: '', confirm: '' }
                setTimeout(() => router.push('/settings'), 2000)
            } catch (err) {
                error.value = err.message || t('auth.change.failed')
            } finally {
                loading.value = false
            }
        }
        return {
            submit,
            form,
            error,
            success,
            loading,
            showBack,
            passwordErrors,
            isStrong,
        }
    }
}
</script>

<style scoped>
/* Layout mirrors login */
.auth-shell { min-height: 100vh; padding: 24px; }
.auth-card { width: 100%; max-width: 540px; }

/* Header row: back button + centered title */
.auth-head{
  display:flex; align-items:center; gap:10px; margin-bottom: 12px;
}
.auth-head .flex-1{ flex:1; }
.back-spacer{ width:82px; } /* roughly the width of the back button to keep title truly centered */

/* Brand outline button (matches your other pages) */
.btn-outline{
  display:inline-flex; align-items:center; gap:8px;
  border:1px solid #4F7EB3; color:#4F7EB3; padding:6px 12px;
  border-radius:8px; text-decoration:none;
  background:#f7fbff;
}
.btn-outline:hover{ background:#4F7EB3; color:#fff }
.back-btn i{ font-size:1rem }

/* Primary action (Storalytic colors) */
.stora-btn-primary{
  display:inline-flex; align-items:center; justify-content:center;
  gap:8px; padding:10px 16px; border-radius:8px;
  background:#4F7EB3; color:#fff; border:0; cursor:pointer;
  transition: background .2s ease, transform .02s ease;
}
.stora-btn-primary:hover{ background:#B2C149; }
.stora-btn-primary:disabled{ opacity:.7; cursor:not-allowed; }

/* Inputs look consistent with login */
.form-control {
  max-width: 500px;
}

/* Messages */
.error-message{ color:#C21500; }
.success-message{ color:#2c7a3f; }

/* Card spacing similar to login */
.card { margin-top: 2rem; }

@media (max-width: 420px){
  .back-spacer{ display:none; }
  .auth-head{ gap:6px; }
  .auth-head h2{ font-size:1.25rem; }
}
</style>